import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, FlipVertical2, RotateCcw } from "lucide-react";
import { api, errCode } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { DamesBoard } from "@/components/DamesBoard";
import { SoundToggle } from "@/components/SoundToggle";
import { THEMES, loadPrefs, savePrefs } from "@/lib/damesBoard";
import { playCapture, playSteps, playHome, playWin } from "@/lib/sound";
import { toast } from "sonner";
import { ShareResult } from "@/components/ShareResult";

const count = (board, c) => Object.values(board).filter((p) => p.toLowerCase() === c).length;

const Prefs = ({ prefs, setPrefs, flip }) => {
  const { t } = useI18n();
  const upd = (p) => { const n = { ...prefs, ...p }; setPrefs(n); savePrefs(n); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dames-prefs">
      {Object.entries(THEMES).map(([k, v]) => (
        <button key={k} type="button" onClick={() => upd({ theme: k })} data-testid={`dames-theme-${k}`}
          className={`h-9 rounded-full px-3 text-xs font-semibold transition-colors ${prefs.theme === k ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10 text-slate-300"}`}>{v.label}</button>
      ))}
      <button type="button" onClick={() => upd({ coords: !prefs.coords })} data-testid="dames-coords-toggle"
        className="h-9 rounded-full border border-white/10 px-3 text-xs font-semibold text-slate-300">{prefs.coords ? t("dames.hideCoords") : t("dames.showCoords")}</button>
      <button type="button" onClick={flip} aria-label={t("dames.flip")} data-testid="dames-flip-btn" className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300"><FlipVertical2 className="h-4 w-4" /></button>
    </div>
  );
};

export default function DamesPracticePage() {
  const { t, tErr } = useI18n();
  const [hist, setHist] = useState([]);
  const [view, setView] = useState(0);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [flipped, setFlipped] = useState(false);
  const [busy, setBusy] = useState(false);
  const start = useCallback(() => api.get("/dames/practice/new").then(({ data }) => { setHist([data]); setView(0); }), []);
  useEffect(() => { start(); }, [start]);

  const cur = hist[hist.length - 1];
  const shown = hist[view];
  const live = view === hist.length - 1;
  const move = async (path) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/dames/practice/play", { state: cur.state, path });
      const l = data.last;
      if (l.captured.length) playCapture(); else playSteps(1);
      if (l.promoted) setTimeout(playHome, 400);
      if (data.state.result) setTimeout(playWin, 700);
      setHist((h) => [...h, data]);
      setView(hist.length);
    } catch (e) { toast.error(tErr(errCode(e))); } finally { setBusy(false); }
  };
  if (!cur) return <div className="py-24 text-center text-slate-500">{t("common.loading")}</div>;
  const st = shown.state;
  const res = cur.state.result;
  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="dames-practice-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold">{t("dames.practiceTitle")}</h1>
          <p className="text-xs text-slate-400">{t("dames.practiceSub")}</p>
        </div>
        <SoundToggle />
      </div>
      <div className="flex items-center justify-between rounded-2xl bg-white/5 px-4 py-2 text-sm" data-testid="dames-status">
        <span><b>{t("dames.black")}</b> · {count(st.board, "b")}</span>
        <span className={`font-semibold ${res ? "text-amber-300" : "text-slate-200"}`} data-testid="dames-turn">
          {res ? (res === "draw" ? t("dames.draw") : t("dames.wins", { c: res === "w" ? t("dames.white") : t("dames.black") }))
            : t("dames.toPlay", { c: st.turn === "w" ? t("dames.white") : t("dames.black") })}
        </span>
        <span><b>{t("dames.white")}</b> · {count(st.board, "w")}</span>
      </div>
      {live && !res && cur.must_capture && <p className="text-center text-sm font-semibold text-amber-300" data-testid="dames-must-capture">{t("dames.mustCapture")}</p>}
      {res && <ShareResult game={t("share.dames")} result={res === "draw" ? t("dames.draw") : t("dames.wins", { c: res === "w" ? t("dames.white") : t("dames.black") })} path="/dames/essai" />}
      <DamesBoard board={st.board} legal={live && !res ? cur.legal : []} onMove={move} flipped={flipped} themeKey={prefs.theme} coords={prefs.coords}
        last={shown.last} animKey={live ? hist.length : 0} disabled={busy || !live} />
      <Prefs prefs={prefs} setPrefs={setPrefs} flip={() => setFlipped((f) => !f)} />
      <div className="flex items-center justify-between gap-2" data-testid="dames-replay">
        <button type="button" onClick={() => setView((v) => Math.max(0, v - 1))} disabled={view === 0} data-testid="dames-prev-btn" className="grid h-11 w-11 place-items-center rounded-full border border-white/10 disabled:opacity-40"><ChevronLeft className="h-5 w-5" /></button>
        <p className="min-w-0 flex-1 truncate text-center font-mono text-xs text-slate-400" data-testid="dames-move-label">
          {view === 0 ? t("dames.startPos") : `${view}. ${shown.last.notation}`} {!live && `· ${t("dames.replay")}`}
        </p>
        <button type="button" onClick={() => setView((v) => Math.min(hist.length - 1, v + 1))} disabled={live} data-testid="dames-next-btn" className="grid h-11 w-11 place-items-center rounded-full border border-white/10 disabled:opacity-40"><ChevronRight className="h-5 w-5" /></button>
      </div>
      <button type="button" onClick={start} data-testid="dames-new-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-amber-500/40 font-semibold text-amber-300"><RotateCcw className="h-4 w-4" /> {t("dames.newGame")}</button>
    </div>
  );
}
